import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, Map, Marker, InfoWindow } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const LoadsMapView = ({ 
  loads, 
  loadIssues, 
  onLoadSelect, 
  activeTab,
  onOpenDetails
}) => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 41.015137, lng: 28.97953 }); // Default to Turkey
  const [zoom, setZoom] = useState(5);
  const mapRef = useRef(null);

  // Filter loads based on active tab
  const filteredLoads = loads.filter((load) => {
    if (activeTab === 'pending') return load.status === 'open';
    if (activeTab === 'driver') return load.status === 'waitingDriver';
    if (activeTab === 'agreed') return load.status === 'assigned';
    if (activeTab === 'completed') return load.status === 'completed';
    return true;
  });

  // Focus map on loads when they change
  useEffect(() => {
    if (filteredLoads.length > 0 && mapRef.current) {
      fitBoundsToMarkers();
    }
  }, [filteredLoads, activeTab]);

  // Function to fit map bounds to markers
  const fitBoundsToMarkers = () => {
    if (!mapRef.current || filteredLoads.length === 0) return;
    
    // Create bounds object
    const bounds = new window.google.maps.LatLngBounds();
    
    // Add each load's coordinates to bounds
    filteredLoads.forEach(load => {
      if (load.loading_latitude && load.loading_longitude) {
        bounds.extend({
          lat: parseFloat(load.loading_latitude),
          lng: parseFloat(load.loading_longitude)
        });
      }
    });
    
    // If we have valid bounds, fit the map to them
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  };

  // Handle marker click
  const handleMarkerClick = (load) => {
    setSelectedMarker(load);
    
    // Center map on the selected marker
    if (load.loading_latitude && load.loading_longitude) {
      setMapCenter({
        lat: parseFloat(load.loading_latitude),
        lng: parseFloat(load.loading_longitude)
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // Handle map load
  const handleMapLoad = (map) => {
    mapRef.current = map;
    fitBoundsToMarkers();
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="h-[600px]">
        <APIProvider apiKey="AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o">
          <Map
            onLoad={handleMapLoad}
            defaultCenter={mapCenter}
            defaultZoom={zoom}
            gestureHandling="greedy"
            options={{
              zoomControl: true,
              mapTypeControl: true,
              scaleControl: true,
              streetViewControl: true,
              rotateControl: true,
              fullscreenControl: true,
            }}
          >
            {filteredLoads.map((load) => {
              if (!load.loading_latitude || !load.loading_longitude) return null;
              
              return (
                <Marker
                  key={load.id}
                  position={{
                    lat: parseFloat(load.loading_latitude),
                    lng: parseFloat(load.loading_longitude),
                  }}
                  onClick={() => handleMarkerClick(load)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="${
                          load.status === 'open' ? '#3B82F6' : 
                          load.status === 'waitingDriver' ? '#F59E0B' : 
                          load.status === 'assigned' ? '#10B981' : 
                          load.status === 'completed' ? '#6366F1' : '#9CA3AF'
                        }" stroke="white" stroke-width="2"/>
                        ${loadIssues[load.id] ? '<circle cx="18" cy="6" r="5" fill="#EF4444" stroke="white" stroke-width="1"/>' : ''}
                      </svg>
                    `)}`,
                    scaledSize: { width: 24, height: 24 },
                    anchor: { x: 12, y: 12 },
                  }}
                />
              );
            })}

            {selectedMarker && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedMarker.loading_latitude),
                  lng: parseFloat(selectedMarker.loading_longitude),
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="p-2 max-w-xs">
                  <div className="font-bold text-blue-600 mb-1">
                    {selectedMarker.formatted_id}
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-semibold">Yükleme: </span>
                    {selectedMarker.loading_country} - {selectedMarker.pickup_location}
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-semibold">Boşaltma: </span>
                    {selectedMarker.discharge_country} - {selectedMarker.delivery_location}
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-semibold">Müşteri: </span>
                    {selectedMarker.customer_name}
                  </div>
                  <div className="text-sm mb-1">
                    <span className="font-semibold">Yükleme Tarihi: </span>
                    {formatDate(selectedMarker.loading_date)}
                  </div>
                  <div className="text-sm mb-2">
                    <span className="font-semibold">Durum: </span>
                    <span className={`${
                      selectedMarker.status === 'open' ? 'text-blue-600' : 
                      selectedMarker.status === 'waitingDriver' ? 'text-amber-600' : 
                      selectedMarker.status === 'assigned' ? 'text-green-600' : 
                      selectedMarker.status === 'completed' ? 'text-indigo-600' : 'text-gray-600'
                    }`}>
                      {selectedMarker.status === 'open' ? 'Atama Bekleyen' : 
                       selectedMarker.status === 'waitingDriver' ? 'Sürücü Cevabı Bekleyen' : 
                       selectedMarker.status === 'assigned' ? 'Onaylanan' : 
                       selectedMarker.status === 'completed' ? 'Tamamlanan' : 'Bilinmiyor'}
                    </span>
                  </div>
                  <button
                    onClick={() => onOpenDetails(selectedMarker)}
                    className="w-full px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                    Detayları Göster
                  </button>
                </div>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
};

export default LoadsMapView;
